import './App.scss';
import { Header } from "./header/header";
import { Navigation } from "./nav/Navigation";
import { Homepage } from "./pages/home/Homepage";
import { Footer } from "./footer/Footer";
import { createBrowserRouter, RouterProvider, Outlet, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { Page } from "./pages/Page";
import { Loader } from "./loader/loader";
import './i18n';
import { useTranslation } from "react-i18next"; // Import the i18n configuration
import { useEffect } from 'react';
import {Post, Posts} from "./pages/posts/Posts";
import { AnalyticsCup } from "./pages/analyticsCup/AnalyticsCup";
import { AnalyticsCupMain } from "./pages/analyticsCup/AnalyticsCupMain";
import { AnalyticsCupRules } from "./pages/analyticsCup/AnalyticsCupRules";
import { AnalyticsCupFAQ } from './pages/analyticsCup/AnalyticsCupFAQ';
import { AnalyticsCupResults } from './pages/analyticsCup/AnalyticsCupResults';
import { AnalyticsCupSubmissions } from './pages/analyticsCup/AnalyticsCupSubmissions';
import { AnalyticsCupVideos } from './pages/analyticsCup/AnalyticsCupVideos';
import { EditionPage } from './pages/analyticsCup/EditionPage';

const ScrollToTop = () => {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        // Edition pages scroll to the section named in the URL themselves;
        // scrolling to top here would run first and turn every section jump
        // into top-then-section.
        if (/^\/analytics-cup\/editions\/[^/]+\/./.test(pathname)) return;
        if (hash) {
            const el = document.getElementById(hash.replace('#', ''));
            if (el) {
                el.scrollIntoView({ behavior: 'instant', block: 'start' });
            }
        } else {
            window.scrollTo({ top: 0, behavior: 'instant' });
        }
    }, [pathname, hash]);
    return null;
};

const Layout = () => (
    <>
        <ScrollToTop />
        <Navigation />
        <Header />
        <Outlet />
        <Footer />
    </>
);

const AnalyticsCupLayout = () => (
    <>
        <ScrollToTop />
        <Outlet />
        <Footer />
    </>
);

// LanguageWrapper component
const LanguageWrapper = ({ children }) => {
    const { i18n } = useTranslation();

    useEffect(() => {
        // Check the current URL path to set language
        const path = window.location.pathname;
        if (path.startsWith('/nl')) {
            i18n.changeLanguage('nl');
        } else {
            i18n.changeLanguage('en'); // Default to English or any other default language
        }
    }, [i18n]);

    return children;
};

// Define routes with the LanguageWrapper in mind
const router = createBrowserRouter([
    {
        path: "/",
        element: <Layout />,
        loader: Loader,
        children: [
            {
                index: true,
                element: <Homepage />,
            },
            {
                path: "/pages/:slug",
                element: <Page />,
                loader: Loader,
            },
            {
                path: "/posts/:slug",
                element: <Post />,
            },
        ],
    },
    {
        path: "/nl",
        element: <Layout />,
        loader: Loader,
        children: [
            {
                index: true,
                element: <Homepage />,
            },
            {
                path: "/nl/pages/:slug",
                element: <Page />,
                loader: Loader,
            },
            {
                path: "/nl/posts/:slug",
                element: <Post />,
            },
        ],
    },
    {
        path: "/analytics-cup",
        element: <AnalyticsCupLayout />,
        children: [
            {
                index: true,
                element: <AnalyticsCupMain />,
            },
            {
                path: "rules",
                element: <AnalyticsCupRules />,
            },
            {
                path: "faq",
                element: <AnalyticsCupFAQ />,
            },
            {
                path: "results",
                element: <AnalyticsCupResults />,
            },
            {
                path: "submissions",
                element: <AnalyticsCupSubmissions />,
            },
            {
                path: "videos",
                element: <AnalyticsCupVideos />,
            },
            {
                // One route with an optional section: two separate entries
                // would remount EditionPage on every section navigation,
                // resetting the scroll position.
                path: "editions/:slug/:sectionId?",
                element: <EditionPage />,
            },
        ],
    },
    // {
    //     path: "/nl/analytics-cup",
    //     element: <AnalyticsCupLayout />,
    //     children: [
    //         {
    //             index: true,
    //             element: <AnalyticsCup />,
    //         }
    //     ],
    // },
]);

function App() {
    return (
        <HelmetProvider>
            <div className="App">
                <LanguageWrapper>
                    <RouterProvider fallbackElement={<p>Loading...</p>} router={router} />
                </LanguageWrapper>
            </div>
        </HelmetProvider>
    );
}

export default App;
